/**
 * Mobile Nav — Off-Canvas Behavior (default pattern)
 *
 * Companion JS to mobile-nav__default.php's off-canvas markup: open/
 * close, overlay, focus trap, and the mobile accordion-style submenu
 * toggle buttons. This is specific to the off-canvas PATTERN, unlike
 * menu__submenu-accessibility.js (core/menu/) which is generic to any
 * mobile-nav pattern -- a future bottom-bar override would bring its
 * OWN JS instead of this file, since bottom-bar has no off-canvas
 * panel, no hamburger toggle, no overlay to manage.
 *
 * KNOWN GAP, not solved here: JS/CSS files aren't part of the
 * override-managed system the way __default.php files are (see
 * project README §3) -- they're swept up by the general alphabetical
 * whole-tree walk regardless of which mobile-nav.php a site is using.
 * That means a future bottom-bar override would need this file
 * EXCLUDED somehow (a zzz_ prefix on this file in that site's build,
 * most likely) or its selectors would silently no-op against markup
 * that no longer exists -- harmless today (querySelector against
 * missing elements just returns null and every function here already
 * guards for that), but worth remembering when bottom-bar actually
 * gets built.
 */

(function () {
  "use strict";

  const CONFIG = {
    selectors: {
      mobileToggle: ".mobile-menu-toggle",
      mobileMenu: "#mobile-menu",
      mobileMenuClose: ".mobile-menu__close",
      overlay: ".mobile-menu-overlay",
    },
    submenuToggle: {
      html: '<button type="button" class="submenu-toggle" aria-expanded="false"><span class="submenu-toggle__icon">▼</span></button>',
      buttonClass: "submenu-toggle",
      iconClass: "submenu-toggle__icon",
    },
  };

  let state = {
    isInitialized: false,
    mobileBreakpoint: null,
    cachedElements: {},
    submenuToggles: new Map(),
  };

  function getMobileBreakpoint() {
    if (state.mobileBreakpoint !== null) return state.mobileBreakpoint;
    const breakpoint = getComputedStyle(document.documentElement)
      .getPropertyValue("--mobile-breakpoint")
      .trim();
    state.mobileBreakpoint = parseFloat(breakpoint) * 16;
    return state.mobileBreakpoint;
  }

  function isMobile() {
    return window.innerWidth <= getMobileBreakpoint();
  }

  function cacheElements() {
    state.cachedElements = {
      mobileToggle: document.querySelector(CONFIG.selectors.mobileToggle),
      mobileMenu: document.querySelector(CONFIG.selectors.mobileMenu),
      mobileMenuClose: document.querySelector(CONFIG.selectors.mobileMenuClose),
      overlay: document.querySelector(CONFIG.selectors.overlay),
    };
    return state.cachedElements;
  }

  function getFocusableElements(container) {
    const elements = container.querySelectorAll(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    return {
      first: elements[0],
      last: elements[elements.length - 1],
      all: Array.from(elements),
    };
  }

  // ── Mobile accordion submenu toggle buttons ──────────────────────

  function addSubmenuToggle(parentItem) {
    const link = parentItem.querySelector(
      ":scope > a, :scope > .menu-item-text",
    );
    const submenu = parentItem.querySelector(":scope > .sub-menu");

    if (!link || !submenu) return;

    if (parentItem.querySelector("." + CONFIG.submenuToggle.buttonClass)) {
      return;
    }

    const buttonWrapper = document.createElement("div");
    buttonWrapper.innerHTML = CONFIG.submenuToggle.html;
    const toggleButton = buttonWrapper.firstChild;

    const submenuId =
      submenu.id || "submenu-" + Math.random().toString(36).substr(2, 9);
    submenu.id = submenuId;
    toggleButton.setAttribute("aria-controls", submenuId);
    toggleButton.setAttribute(
      "aria-label",
      `Toggle ${link.textContent.trim()} submenu`,
    );

    link.parentNode.insertBefore(toggleButton, link.nextSibling);

    state.submenuToggles.set(toggleButton, { parentItem, submenu, link });

    toggleButton.addEventListener("click", handleSubmenuToggle);
    link.addEventListener("click", handleParentLinkClick);

    // aria-hidden alone doesn't remove focusability -- inert does both
    // (removes from tab order AND the accessibility tree) in one step.
    // Without it, a collapsed submenu's links stay Tab-reachable while
    // simultaneously being announced as hidden to screen readers.
    submenu.setAttribute("aria-hidden", "true");
    submenu.inert = true;

    updateSubmenuToggleVisibility(toggleButton);
  }

  function handleSubmenuToggle(e) {
    e.preventDefault();
    e.stopPropagation();

    const button = e.currentTarget;
    const data = state.submenuToggles.get(button);
    if (!data) return;

    const { parentItem, submenu } = data;
    const isOpen = parentItem.classList.contains("is-open");

    const siblings = parentItem.parentNode.querySelectorAll(
      ":scope > .menu-item.is-open",
    );
    siblings.forEach((sibling) => {
      if (sibling !== parentItem) {
        closeSubmenu(sibling);
      }
    });

    if (isOpen) {
      closeSubmenu(parentItem);
    } else {
      openSubmenu(parentItem);
    }
  }

  function handleParentLinkClick(e) {
    const link = e.currentTarget;
    const href = link.getAttribute("href");
    const isSpan = link.tagName.toLowerCase() === "span";
    if (isSpan || href === "#" || href === "") {
      e.preventDefault();
    }
  }

  function openSubmenu(parentItem) {
    parentItem.classList.add("is-open");

    const button = parentItem.querySelector(
      "." + CONFIG.submenuToggle.buttonClass,
    );
    if (button) {
      button.setAttribute("aria-expanded", "true");
      const icon = button.querySelector("." + CONFIG.submenuToggle.iconClass);
      if (icon) icon.style.transform = "rotate(180deg)";
    }
    const submenu = parentItem.querySelector(":scope > .sub-menu");
    if (submenu) {
      submenu.setAttribute("aria-hidden", "false");
      submenu.inert = false;
    }
  }

  function closeSubmenu(parentItem) {
    parentItem.classList.remove("is-open");

    const button = parentItem.querySelector(
      "." + CONFIG.submenuToggle.buttonClass,
    );
    if (button) {
      button.setAttribute("aria-expanded", "false");
      const icon = button.querySelector("." + CONFIG.submenuToggle.iconClass);
      if (icon) icon.style.transform = "";
    }
    const submenu = parentItem.querySelector(":scope > .sub-menu");
    if (submenu) {
      submenu.setAttribute("aria-hidden", "true");
      submenu.inert = true;
    }
  }

  function updateSubmenuToggleVisibility(button) {
    button.style.display = isMobile() ? "" : "none";
  }

  function initSubmenuToggles() {
    const { mobileMenu } = state.cachedElements;
    if (!mobileMenu) return;

    const parentItems = mobileMenu.querySelectorAll(".menu-item.has-children");
    parentItems.forEach(addSubmenuToggle);
  }

  // ── Open/close, overlay, focus trap ───────────────────────────────
  //
  // Every aria-hidden toggle in this file (panel + submenus) is paired
  // with .inert. aria-hidden alone only tells assistive tech to skip
  // an element -- it does nothing to actual keyboard focusability. The
  // panel is hidden via transform (translateX), not display:none, so
  // its buttons/links stay Tab-reachable even while off-screen unless
  // something explicitly removes them from the tab order -- that's
  // exactly what .inert does, in addition to (not instead of)
  // aria-hidden. Without it: a keyboard user tabbing through a closed
  // page can land inside content simultaneously announced as hidden
  // to screen reader users.

  function openMenu() {
    const { mobileToggle, mobileMenu, mobileMenuClose, overlay } =
      state.cachedElements;

    mobileToggle.setAttribute("aria-expanded", "true");
    mobileMenu.classList.add("is-open");
    mobileMenu.setAttribute("aria-hidden", "false");
    mobileMenu.inert = false;
    if (overlay) overlay.classList.add("is-visible");

    document.body.style.overflow = "hidden";

    if (mobileMenuClose) {
      setTimeout(() => mobileMenuClose.focus(), 100);
    }
  }

  function closeMenu() {
    const { mobileToggle, mobileMenu, overlay } = state.cachedElements;

    mobileToggle.setAttribute("aria-expanded", "false");
    mobileMenu.classList.remove("is-open");
    mobileMenu.setAttribute("aria-hidden", "true");
    mobileMenu.inert = true;
    if (overlay) overlay.classList.remove("is-visible");

    document.body.style.overflow = "";

    mobileToggle.focus();

    const openSubmenus = mobileMenu.querySelectorAll(".menu-item.is-open");
    openSubmenus.forEach(closeSubmenu);
  }

  function toggleMenu() {
    const { mobileMenu, mobileToggle } = state.cachedElements;

    if (mobileMenu.classList.contains("is-open")) {
      closeMenu();
    } else {
      openMenu();
    }

    if (mobileToggle) {
      mobileToggle.classList.add("is-animating");
      setTimeout(() => {
        mobileToggle.classList.remove("is-animating");
      }, 300);
    }
  }

  function handleCloseButtonClick(e) {
    const button = e.currentTarget;
    button.classList.add("is-animating");
    setTimeout(() => {
      button.classList.remove("is-animating");
    }, 250);

    closeMenu();
  }

  function handleMenuFocusTrap(e) {
    const { mobileMenu } = state.cachedElements;

    if (e.key !== "Tab" || !mobileMenu.classList.contains("is-open")) return;

    const focusable = getFocusableElements(mobileMenu);

    if (e.shiftKey) {
      if (document.activeElement === focusable.first) {
        e.preventDefault();
        focusable.last.focus();
      }
    } else {
      if (document.activeElement === focusable.last) {
        e.preventDefault();
        focusable.first.focus();
      }
    }
  }

  function initMobileMenu() {
    const { mobileToggle, mobileMenuClose, mobileMenu, overlay } =
      state.cachedElements;

    if (!mobileToggle || !mobileMenu) return;

    // Panel starts closed on every page load -- see the panel-level
    // note above openMenu()/closeMenu() for why both attributes are
    // needed together, not just aria-hidden.
    mobileMenu.setAttribute("aria-hidden", "true");
    mobileMenu.inert = true;

    mobileToggle.addEventListener("click", toggleMenu);

    if (mobileMenuClose) {
      mobileMenuClose.addEventListener("click", handleCloseButtonClick);
    }

    if (overlay) {
      overlay.addEventListener("click", closeMenu);
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && mobileMenu.classList.contains("is-open")) {
        closeMenu();
      }
    });

    mobileMenu.addEventListener("keydown", handleMenuFocusTrap);

    mobileMenu.addEventListener("click", (e) => {
      const link = e.target.closest("a");
      if (link && !link.closest(".menu-item.has-children > a")) {
        closeMenu();
      }
      if (link && link.closest(".sub-menu")) {
        closeMenu();
      }
    });
  }

  function handleResize() {
    state.submenuToggles.forEach((data, button) => {
      updateSubmenuToggleVisibility(button);
    });
  }

  function init() {
    if (state.isInitialized) return;
    cacheElements();
    initMobileMenu();
    initSubmenuToggles();

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    });

    state.isInitialized = true;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();